import chevronRightIcon from "@loot-os/icons/icons/chevron-right";
import { Icon } from "../../core/Icon.js";
import { AppLink } from "../../foundations/AppLink.js";
import { cn } from "../../foundations/cn.js";
import { FOCUS_RING } from "../../foundations/interaction.js";
import { OptionalImage } from "../../foundations/OptionalImage.js";
import { NotificationCategoryIcon } from "./NotificationCategoryIcon.js";
import type { NotificationRowContent } from "./notificationTypes.js";

export interface NotificationRowProps {
  notification: NotificationRowContent;
  /** Read by screen readers beside the unread dot. */
  unreadLabel?: string;
}

const ROW = cn(
  "flex w-full cursor-pointer items-start gap-3 rounded-xl px-2.5 py-2.5 text-left",
  "transition-colors duration-fast ease-standard hover:bg-bg-hover active:bg-bg-pressed motion-reduce:transition-none",
  FOCUS_RING,
);

/**
 * One notification: its icon card, title, body, optional banner and action words, its age and an
 * unread dot. The whole row is the link (in the app, or a new tab) or, with nowhere to go, a
 * button; pressing it marks it read.
 */
export function NotificationRow({ notification, unreadLabel = "Unread" }: NotificationRowProps) {
  const n = notification;
  const content = (
    <>
      <NotificationCategoryIcon category={n.category} icon={n.icon} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className={cn(
            "line-clamp-2 font-body",
            n.unread ? "text-body-b1-strong text-text-primary" : "text-body-b1 text-text-secondary",
          )}
        >
          {n.title}
        </span>
        {n.body ? (
          <span className="line-clamp-3 font-body text-body-b2 text-text-secondary">{n.body}</span>
        ) : null}
        {n.imageUrl ? (
          <OptionalImage
            src={n.imageUrl}
            alt=""
            loading="lazy"
            className="mt-2 aspect-[3/1] w-full rounded-md bg-bg-inset object-cover"
          />
        ) : null}
        {n.actionLabel ? (
          <span className="mt-1.5 inline-flex items-center gap-0.5 font-body text-body-b2-strong text-accent-text">
            {n.actionLabel}
            <Icon icon={chevronRightIcon} size={14} />
          </span>
        ) : null}
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1.5 pt-0.5">
        <time dateTime={n.createdAt} className="font-mono text-mono-meta text-text-secondary">
          {n.age}
        </time>
        {n.unread ? (
          <span className="size-2 rounded-pill bg-accent-default">
            <span className="sr-only">{unreadLabel}</span>
          </span>
        ) : null}
      </span>
    </>
  );

  if (!n.target) {
    return (
      <button type="button" className={ROW} onClick={n.onSelect}>
        {content}
      </button>
    );
  }
  if (n.target.external) {
    return (
      <a
        href={n.target.href}
        target="_blank"
        rel="noopener noreferrer"
        className={ROW}
        onClick={n.onSelect}
      >
        {content}
      </a>
    );
  }
  return (
    <AppLink href={n.target.href} className={ROW} onClick={n.onSelect}>
      {content}
    </AppLink>
  );
}
